import { api } from './api'
import type { ApiResponse } from '../types'
import { UpdateFrequency, ConnectionStatus } from '../types'

interface WeeklyScheduleDay {
  enabled: boolean
  time?: string
}

interface WeeklySchedule {
  monday: WeeklyScheduleDay
  tuesday: WeeklyScheduleDay
  wednesday: WeeklyScheduleDay
  thursday: WeeklyScheduleDay
  friday: WeeklyScheduleDay
  saturday: WeeklyScheduleDay
  sunday: WeeklyScheduleDay
}

export interface ArrivalSchedule {
  schedule_type: 'daily' | 'weekly'
  daily_time?: string
  weekly_schedule?: WeeklySchedule
  timezone: string
}

export interface DataSource {
  id: number
  source_name: string
  data_category_id: number
  source_type_id: number
  ingestion_method: string
  update_frequency: string
  source_location: string
  context: string
  connection_status: string
  priority: number
  last_sync_utc: string | null
  created_at: string
  updated_at: string
  created_by: string
  updated_by: string
  deleted_at: string | null
  deleted_by: string | null
  category_name: string
  category_code: string
  category_type: string
  source_type_code: string
  source_type_display_name: string
  source_type_category: string
  arrival_schedule?: ArrivalSchedule | null

  // S3 file upload information (optional)
  file_path_s3?: string
  s3_key?: string
  s3_bucket?: string
  original_filename?: string
  upload_id?: number
  first_upload_id?: number | null

  // Display properties
  name?: string // Computed from source_name
  description?: string // Computed from context
  abbreviation?: string // Computed or generated
  status?: 'connected' | 'available' // Computed from connection_status
  color?: string // For display purposes
}

export interface DataSourcesQuery {
  page?: number
  limit?: number
  search?: string
  status?: string
  type?: string
}

export interface DataSourcesApiResponse {
  success: boolean
  message: string
  data: DataSource[]
  total_count: number
  page: number
  per_page: number
  has_more: boolean
}

export interface CreateDataSourceRequest {
  source_name: string
  source_type_id: number
  data_category_id: number
  ingestion_method: string
  update_frequency: UpdateFrequency
  source_location: string
  context: string
  connection_status: ConnectionStatus
  priority: number
}

export interface CreateDataSourceResponse {
  success: boolean
  message: string
  data: DataSource
}

export class DataSourcesService {
  /**
   * Get all data sources with optional filtering
   */
  static async getDataSources(
    query: DataSourcesQuery = {}
  ): Promise<DataSourcesApiResponse> {
    const params = new URLSearchParams()

    if (query.page) params.append('page', query.page.toString())
    if (query.limit) params.append('per_page', query.limit.toString()) // API uses per_page
    if (query.search) params.append('search', query.search)
    if (query.status) params.append('status', query.status)
    if (query.type) params.append('type', query.type)

    const response = await api.get<DataSourcesApiResponse>(
      `/data-sources/?${params.toString()}`
    )

    return response.data
  }

  /**
   * Get a single data source by ID
   */
  static async getDataSource(id: string): Promise<DataSource> {
    const response = await api.get<ApiResponse<DataSource>>(`/data-sources/${id}`)
    return response.data.data
  }

  /**
   * Create a new data source
   */
  static async createDataSource(
    data: CreateDataSourceRequest
  ): Promise<CreateDataSourceResponse> {
    const response = await api.post<CreateDataSourceResponse>('/data-sources/', data)
    return response.data
  }

  /**
   * Update an existing data source
   */
  static async updateDataSource(
    id: string,
    data: Partial<Omit<DataSource, 'id' | 'createdAt' | 'updatedAt'>>
  ): Promise<DataSource> {
    const response = await api.put<ApiResponse<DataSource>>(`/data-sources/${id}`, data)
    return response.data.data
  }

  /**
   * Delete a data source
   */
  static async deleteDataSource(id: string): Promise<void> {
    await api.delete(`/data-sources/${id}`)
  }

  /**
   * Test connection to a data source
   */
  static async testConnection(id: string): Promise<{ success: boolean; message: string }> {
    const response = await api.post<ApiResponse<{ success: boolean; message: string }>>(
      `/data-sources/${id}/test-connection`
    )
    return response.data.data
  }

  /**
   * Update data source priorities
   */
  static async updatePriorities(
    priorities: Array<{ data_source_id: number; priority: number }>,
    dataCategoryId: number
  ): Promise<ApiResponse<any>> {
    const response = await api.patch<ApiResponse<any>>('/data-sources/priorities', {
      data_category_id: dataCategoryId,
      priorities
    })
    return response.data
  }
}